/**
 * Download CSV Modal Component
 *
 * Modal for exporting requests to CSV with filter options.
 * For Product Catalog exports, includes a record-by-record preview viewer.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import { TYPE_DISPLAY_NAMES } from '../config/requestTypeConstants';
import './DownloadCSVModal.css';

const PRODUCT_CATALOG = 'PRODUCT_CATALOG';

// Display names for the export dropdown — PPP shown with its full name.
// GPR subtypes already have "GPR - " prefix in TYPE_DISPLAY_NAMES.
const EXPORT_DISPLAY_NAMES = {
  ...TYPE_DISPLAY_NAMES,
  PPP: 'Product Packaging & Pricing',
};

// Type codes to suppress from the dropdown (legacy duplicates / parent roll-ups)
const EXPORT_EXCLUDED = new Set(['GPR']);

const PRODUCT_CSV_COLUMNS = [
  { key: 'id',             label: 'ID' },
  { key: 'product_name',   label: 'Product Name' },
  { key: 'product_code',   label: 'Product Code' },
  { key: 'product_type',   label: 'Product Type' },
  { key: 'release_type',   label: 'Release Type' },
  { key: 'categorization', label: 'Categorization' },
  { key: 'description',    label: 'Description' },
  { key: 'status',         label: 'Status' },
  { key: 'owner',          label: 'Owner' },
];

// Field groupings for the preview card (matches exportService flattenProductCatalogRow)
const PREVIEW_SECTIONS = [
  {
    title: 'Basic',
    fields: ['Product Name', 'Product Code', 'Product Type', 'Release Type', 'Categorization', 'Description', 'Status', 'Owner'],
  },
  {
    title: 'Commercial Detail',
    fields: ['Business Unit', 'Performance Matrix Group', 'Product Family', 'Based On', 'Availability Date', 'Installation', 'End Of Life Date', 'Discontinued Date', 'Core Product Type', 'Predecessor Product'],
  },
  {
    title: 'Technical Detail',
    fields: ['Fulfilment Qty Calculation Method', 'PPP Apply'],
  },
  {
    title: 'Provisioning',
    fields: ['Provisioning Required', 'Provisioning Team', 'Provisioning Automated', 'Provisioned in NPE/Prod', 'Has Provisioning Action on Renewal'],
  },
  {
    title: 'Pricing & Packaging',
    fields: [
      'P&P: Premium Stair Step Schedule', 'P&P: Royalty/Revshare Product',
      'P&P: Royalty/Revshare Type', 'P&P: Royalty/Revshare Vendor Name',
      'P&P: Exclude Header Discounts', 'P&P: Invoice Group Description', 'P&P: Invoice Group Code',
      'P&P: Product Prefix', 'P&P: Product Tax Code', 'P&P: Product Tax Description',
      'P&P: Contributes to Price Increases', 'P&P: Assign Price', 'P&P: Quantity Restricted',
      'P&P: Default Quantity', 'P&P: Excluded from ARR', 'P&P: Product Depends On',
      'P&P: Sell Only With Other Products', 'P&P: Is Assetizable', 'P&P: Unit of Measure',
      'P&P: Revenue Account', 'P&P: Program Association Cardinality', 'P&P: Program Association Type',
      'P&P: Program Association Root Product', 'P&P: Configure During Sale', 'P&P: SKU Sub Type',
      'P&P: HH Baseline Metric', 'P&P: Non-Renewable', 'P&P: PP Status',
      'P&P: EA to GA Strategy', 'P&P: Autopush SKU',
    ],
  },
];

const DownloadCSVModal = ({ onClose }) => {
  const [skiReleases, setSkiReleases] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [selectedSkiRelease, setSelectedSkiRelease] = useState('all');
  const [selectedRequestType, setSelectedRequestType] = useState('all');
  const [recordCount, setRecordCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingCount, setFetchingCount] = useState(false);
  const [error, setError] = useState(null);

  // Preview state
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const isProductCatalog = selectedRequestType === PRODUCT_CATALOG;

  /**
   * Fetch filter options on mount
   */
  useEffect(() => {
    fetchFilterOptions();
    fetchRecordCount('all', 'all'); // Initial count
  }, []);

  /**
   * Fetch record count when filters change
   */
  useEffect(() => {
    if (selectedRequestType === PRODUCT_CATALOG) {
      fetchProductCount();
    } else {
      fetchRecordCount(selectedSkiRelease, selectedRequestType);
    }
  }, [selectedSkiRelease, selectedRequestType]);

  /**
   * Fetch available SKI releases and request types
   */
  const fetchFilterOptions = async () => {
    try {
      // Fetch SKI releases from requests
      const requestsResponse = await api.getRequests({ page: 1, limit: 1000 });
      const requests = requestsResponse.data.data.requests;

      // Extract unique SKI releases
      const uniqueSkiReleases = [];
      const skiReleaseIds = new Set();
      requests.forEach((req) => {
        if (req.skiRelease && !skiReleaseIds.has(req.skiRelease.id)) {
          skiReleaseIds.add(req.skiRelease.id);
          uniqueSkiReleases.push({
            id: req.skiRelease.id,
            name: req.skiRelease.release_name || req.skiRelease.full_name,
          });
        }
      });

      // Sort alphabetically
      uniqueSkiReleases.sort((a, b) => a.name.localeCompare(b.name));

      setSkiReleases(uniqueSkiReleases);

      // Fetch request types
      const filterOptionsResponse = await api.getFilterOptions();
      setRequestTypes(filterOptionsResponse.data.data.requestTypes || []);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      setError('Failed to load filter options');
    }
  };

  /**
   * Fetch product count for Product Catalog option (SKI-release-aware)
   */
  const fetchProductCount = async () => {
    try {
      setFetchingCount(true);
      const params = {};
      if (selectedSkiRelease !== 'all') params.skiReleaseId = selectedSkiRelease;
      const response = await api.getProductCatalogCount(params);
      setRecordCount(response.data.data.count || 0);
    } catch (err) {
      console.error('Error fetching product count:', err);
      setRecordCount(0);
    } finally {
      setFetchingCount(false);
    }
  };

  /**
   * Fetch count of records matching filters
   */
  const fetchRecordCount = async (skiReleaseId, requestType) => {
    try {
      setFetchingCount(true);
      const params = {};
      if (skiReleaseId !== 'all') params.skiReleaseId = skiReleaseId;
      if (requestType !== 'all') params.requestType = requestType;

      const response = await api.getExportCount(params);
      setRecordCount(response.data.data.count);
    } catch (err) {
      console.error('Error fetching record count:', err);
      setRecordCount(0);
    } finally {
      setFetchingCount(false);
    }
  };

  /**
   * Open preview — fetch product catalog data and enter preview mode
   */
  const handlePreview = async () => {
    try {
      setLoadingPreview(true);
      setError(null);
      const params = {};
      if (selectedSkiRelease !== 'all') params.skiReleaseId = selectedSkiRelease;
      const response = await api.exportProductCatalog(params);
      const products = response.data.data.products || [];
      if (products.length === 0) {
        setError('No products to preview');
        return;
      }
      setPreviewData(products);
      setPreviewIndex(0);
      setPreviewMode(true);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError('Failed to load preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  /**
   * Handle download CSV
   */
  const handleDownload = async () => {
    if (recordCount === 0) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isProductCatalog) {
        // Product Catalog — SKI-release-aware export with full product + P&P data
        const params = {};
        if (selectedSkiRelease !== 'all') params.skiReleaseId = selectedSkiRelease;

        // Reuse already-fetched preview data if available, otherwise fetch fresh
        let products = previewData.length > 0 ? previewData : null;
        if (!products) {
          const response = await api.exportProductCatalog(params);
          products = response.data.data.products || [];
        }

        if (products.length === 0) {
          setError('No products to export');
          return;
        }

        const skiName = selectedSkiRelease === 'all'
          ? 'All'
          : (skiReleases.find(s => s.id === selectedSkiRelease)?.name || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_');
        const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        downloadCSV(generateCSV(products), `CPLS_ProductCatalog_${skiName}_${dateStr}.csv`);
      } else {
        // Request export
        const params = {};
        if (selectedSkiRelease !== 'all') params.skiReleaseId = selectedSkiRelease;
        if (selectedRequestType !== 'all') params.requestType = selectedRequestType;

        const response = await api.exportRequests(params);
        const data = response.data.data.requests;

        if (data.length === 0) {
          setError('No records to export');
          return;
        }

        const skiReleaseName = selectedSkiRelease === 'all'
          ? 'All'
          : skiReleases.find(s => s.id === selectedSkiRelease)?.name || 'Unknown';
        const requestTypeName = selectedRequestType === 'all' ? 'All' : selectedRequestType;
        downloadCSV(generateCSV(data), formatFilename(skiReleaseName, requestTypeName));
      }

      setTimeout(() => { onClose(); }, 500);
    } catch (err) {
      console.error('Error downloading CSV:', err);
      setError('Failed to download CSV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate CSV content from data
   */
  const generateCSV = (data) => {
    if (data.length === 0) return '';

    // Get all unique keys from all records
    const allKeys = new Set();
    data.forEach((row) => {
      Object.keys(row).forEach((key) => allKeys.add(key));
    });

    const headers = Array.from(allKeys);

    // Create CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            // Handle null/undefined
            if (value === null || value === undefined) return '';
            // Handle strings with commas, quotes, or newlines
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(',')
      ),
    ];

    return csvRows.join('\n');
  };

  /**
   * Generate CSV for product catalog
   */
  const generateProductCSV = (products) => {
    const escapeCell = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const headers = PRODUCT_CSV_COLUMNS.map(col => col.label).join(',');
    const rows = products.map(p => {
      const ownerName = p.owner
        ? `${p.owner.first_name || ''} ${p.owner.last_name || ''}`.trim()
        : '';
      const values = {
        id: p.id,
        product_name: p.product_name,
        product_code: p.product_code,
        product_type: p.product_type,
        release_type: p.release_type,
        categorization: p.categorization,
        description: p.description,
        status: p.status,
        owner: ownerName,
      };
      return PRODUCT_CSV_COLUMNS.map(col => escapeCell(values[col.key])).join(',');
    });

    return [headers, ...rows].join('\n');
  };

  /**
   * Format filename with timestamp
   */
  const formatFilename = (skiReleaseName, requestTypeName) => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const dateTime = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const skiName = skiReleaseName.replace(/[^a-zA-Z0-9]/g, '_');
    const reqType = requestTypeName.replace(/[^a-zA-Z0-9]/g, '_');

    // P&P uses a specific filename format
    if (selectedRequestType === 'PRICING_PACKAGING') {
      return `PP_${skiName}_${dateStr}.csv`;
    }

    return `CPLS_${skiName}_${reqType}_${dateTime}.csv`;
  };

  /**
   * Trigger browser download
   */
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // ── Preview card renderer ──────────────────────────────────────────────────

  const renderPreview = () => {
    const record = previewData[previewIndex];
    if (!record) return null;

    return (
      <>
        <div className="modal-header">
          <h2>Product Catalog Preview</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="preview-nav">
          <button
            className="preview-nav-btn"
            onClick={() => setPreviewIndex(i => i - 1)}
            disabled={previewIndex === 0}
          >
            ‹
          </button>
          <span className="preview-nav-counter">
            Record {previewIndex + 1} of {previewData.length}
          </span>
          <button
            className="preview-nav-btn"
            onClick={() => setPreviewIndex(i => i + 1)}
            disabled={previewIndex === previewData.length - 1}
          >
            ›
          </button>
        </div>

        <div className="preview-body">
          {PREVIEW_SECTIONS.map(section => {
            const populated = section.fields.filter(f => record[f] != null && record[f] !== '');
            return (
              <div key={section.title} className="preview-section">
                <div className="preview-section-title">{section.title}</div>
                {populated.length === 0 ? (
                  <div className="preview-empty">—</div>
                ) : (
                  populated.map(fieldName => (
                    <div key={fieldName} className="preview-field-row">
                      <span className="preview-field-label">{fieldName.replace('P&P: ', '')}</span>
                      <span className="preview-field-value">{record[fieldName]}</span>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>

        <div className="modal-footer">
          <button className="modal-button modal-button-secondary" onClick={() => setPreviewMode(false)}>
            ← Back
          </button>
          <button
            className="modal-button modal-button-primary"
            onClick={handleDownload}
            disabled={loading}
          >
            {loading ? (
              <><span className="modal-spinner-small"></span>Downloading...</>
            ) : (
              `Download CSV (${previewData.length} records)`
            )}
          </button>
        </div>
      </>
    );
  };

  // ── Filter step renderer ───────────────────────────────────────────────────

  const renderFilters = () => (
    <>
      <div className="modal-header">
        <h2>Export Requests to CSV</h2>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>

      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}

        <div className="modal-field">
          <label htmlFor="requestType">Request Type:</label>
          <select
            id="requestType"
            value={selectedRequestType}
            onChange={(e) => setSelectedRequestType(e.target.value)}
            className="modal-select"
          >
            <option value="all">All Request Types</option>
            <option value={PRODUCT_CATALOG}>Product Catalog</option>
            {requestTypes
              .filter(type => !EXPORT_EXCLUDED.has(type))
              .map((type) => (
                <option key={type} value={type}>
                  {EXPORT_DISPLAY_NAMES[type] || type}
                </option>
              ))}
          </select>
        </div>

        <div className="modal-field">
          <label htmlFor="skiRelease">SKI Release:</label>
          <select
            id="skiRelease"
            value={selectedSkiRelease}
            onChange={(e) => setSelectedSkiRelease(e.target.value)}
            className="modal-select"
          >
            <option value="all">All SKI Releases</option>
            {skiReleases.map((ski) => (
              <option key={ski.id} value={ski.id}>
                {ski.name}
              </option>
            ))}
          </select>
          {isProductCatalog && selectedSkiRelease === 'all' && (
            <span className="modal-field-hint">Select a SKI Release to filter products by release and include matching P&amp;P data.</span>
          )}
        </div>

        <div className="modal-preview">
          <div className="modal-preview-row">
            <span className="modal-preview-label">Records to export:</span>
            <span className="modal-preview-value">
              {fetchingCount ? (
                <span className="modal-spinner-small"></span>
              ) : (
                <strong>{recordCount} {isProductCatalog ? 'products' : 'requests'}</strong>
              )}
            </span>
          </div>
          <div className="modal-preview-row">
            <span className="modal-preview-label">Sorted by:</span>
            <span className="modal-preview-value">Product Name</span>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button className="modal-button modal-button-secondary" onClick={onClose}>
          Cancel
        </button>
        {isProductCatalog && recordCount > 0 && (
          <button
            className="modal-button modal-button-preview"
            onClick={handlePreview}
            disabled={loadingPreview || fetchingCount}
          >
            {loadingPreview ? (
              <><span className="modal-spinner-small"></span>Loading...</>
            ) : (
              'Preview Records'
            )}
          </button>
        )}
        <button
          className="modal-button modal-button-primary"
          onClick={handleDownload}
          disabled={loading || recordCount === 0}
        >
          {loading ? (
            <><span className="modal-spinner-small"></span>Downloading...</>
          ) : (
            'Download CSV'
          )}
        </button>
      </div>
    </>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-content${previewMode ? ' modal-content--wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {previewMode ? renderPreview() : renderFilters()}
      </div>
    </div>
  );
};

DownloadCSVModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default DownloadCSVModal;
